import React from 'react';
import { BookOpen, ScrollText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { personListSubtitle as formatPersonSubtitle } from '../utils/personDisplay';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';

const SIZE_MAP = {
  xs: { wrap: 'w-7 h-7 border border-border/30', text: 'text-[10px]', icon: 'h-2.5 w-2.5', corner: 'gap-0.5' },
  sm: { wrap: 'w-9 h-9 border border-border/30', text: 'text-xs', icon: 'h-3 w-3', corner: 'gap-0.5' },
  md: { wrap: 'w-12 h-12 border border-border/30', text: 'text-base', icon: 'h-3.5 w-3.5', corner: 'gap-0.5' },
  lg: { wrap: 'w-14 h-14 border border-border/40', text: 'text-lg', icon: 'h-4 w-4', corner: 'gap-1' },
  xl: {
    wrap: 'w-32 h-32 border-2 border-border/40',
    text: 'text-4xl',
    icon: 'h-4 w-4',
    corner: 'gap-1',
  },
};

function storyCountTitle(n) {
  if (n <= 0) return undefined;
  return n === 1 ? '1 story' : `${n} stories`;
}

function documentTitle(n) {
  if (n <= 0) return undefined;
  return n === 1 ? '1 document' : `${n} documents`;
}

/**
 * Quiet metadata row for tree cards — stories and documents below the name, not on the photo.
 */
export function PersonArchiveActivityRow({ storyCount = 0, documentCount = 0, className = '' }) {
  const hasStories = storyCount > 0;
  const hasDocs = documentCount > 0;
  if (!hasStories && !hasDocs) return null;

  return (
    <div
      className={cn(
        'mt-1.5 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-[9px] text-muted-foreground/70',
        className
      )}
    >
      {hasStories ? (
        <span className="inline-flex items-center gap-0.5" title={storyCountTitle(storyCount)}>
          <BookOpen className="h-2.5 w-2.5 shrink-0" strokeWidth={1.5} aria-hidden />
          <span>
            {storyCount} {storyCount === 1 ? 'story' : 'stories'}
          </span>
        </span>
      ) : null}
      {hasStories && hasDocs ? <span aria-hidden>·</span> : null}
      {hasDocs ? (
        <span className="inline-flex items-center gap-0.5" title={documentTitle(documentCount)}>
          <ScrollText className="h-2.5 w-2.5 shrink-0" strokeWidth={1.5} aria-hidden />
          <span>
            {documentCount} {documentCount === 1 ? 'doc' : 'docs'}
          </span>
        </span>
      ) : null}
    </div>
  );
}

/**
 * Avatar with optional activity chips (stories / documents), bottom corner, max two.
 * Indicators render only when the corresponding count > 0.
 */
export function PersonAvatarWithIndicators({
  photoUrl,
  initials,
  size = 'md',
  storyCount = 0,
  documentCount = 0,
  className = '',
  /** Use `left` when another control (e.g. photo button) sits on the bottom-right */
  indicatorAlign = 'right',
  showActivityIndicators = true,
  ...rest
}) {
  const s = SIZE_MAP[size] || SIZE_MAP.md;
  const hasStories = storyCount > 0;
  const hasDocs = documentCount > 0;
  const showCorner = showActivityIndicators && (hasStories || hasDocs);

  const chipClass =
    'rounded-full bg-background/92 border border-border/50 shadow-sm p-0.5 text-muted-foreground pointer-events-auto';

  return (
    <div className={cn('relative inline-flex shrink-0', className)} {...rest}>
      <div
        className={cn(
          'rounded-full bg-secondary/40 flex items-center justify-center overflow-hidden shadow-inner',
          s.wrap
        )}
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className={cn('font-serif text-primary/60 select-none', s.text)}>{initials}</span>
        )}
      </div>

      {showCorner && (
        <div
          className={cn(
            'absolute -bottom-0.5 flex items-center pointer-events-none',
            indicatorAlign === 'left' ? '-left-0.5' : '-right-0.5',
            s.corner
          )}
          aria-hidden
        >
          {hasStories && (
            <span className={chipClass} title={storyCountTitle(storyCount)}>
              <BookOpen className={s.icon} strokeWidth={1.75} aria-hidden />
            </span>
          )}
          {hasDocs && (
            <span className={chipClass} title={documentTitle(documentCount)}>
              <ScrollText className={s.icon} strokeWidth={1.75} aria-hidden />
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Single row for SelectItem: avatar + name + optional subtitle (for dropdowns).
 */
export function PersonSelectRow({
  person,
  photoBaseUrl = '',
  subtitle,
  size = 'sm',
  storyCount,
  documentCount,
}) {
  const photoUrl = person?.profile_image_url
    ? resolveAssetUrl(photoBaseUrl, person.profile_image_url)
    : null;
  const initials = `${person?.given_names?.charAt(0) || ''}${person?.surname?.charAt(0) || ''}`;
  const line = subtitle ?? formatPersonSubtitle(person);
  const name = `${person?.given_names || ''} ${person?.surname || ''}`.trim() || 'Unknown';

  return (
    <div className="flex items-center gap-2.5 min-w-0 py-0.5">
      <PersonAvatarWithIndicators
        photoUrl={photoUrl}
        initials={initials}
        size={size}
        storyCount={storyCount ?? 0}
        documentCount={documentCount ?? 0}
      />
      <div className="min-w-0 flex-1 text-left">
        <div className="text-sm font-medium leading-tight truncate">{name}</div>
        {line ? (
          <div className="text-xs text-muted-foreground truncate mt-0.5">{line}</div>
        ) : null}
      </div>
    </div>
  );
}
